/**
 * AI 에이전트 백엔드 클라이언트 — **공통 패키지의 얇은 배선.**
 *
 * 예전에는 이 파일이 103줄짜리 구현이었다. 같은 구현이 openflow 에 TypeScript 사본으로,
 * 그리고 QBase·datamodeler 에 또 한 벌씩 있었다. 구현은 `@qbase/assistant-ui` 로 올라갔고
 * 여기 남는 것은 **이 앱의 설정을 묶고 옛 이름을 맞춰 주는 일**뿐이다.
 *
 * LiveSQL 은 아직 **자체 백엔드**(`/sql-developer/assistant`, :3003)를 쓴다 —
 * 공통 `assistant-service`(:3010)가 아니다. 그래서 두 가지를 여기서 흡수한다:
 *   · 마운트 경로가 다르다        → basePath
 *   · 테넌트 개념이 없다          → tenantId 를 비운다(요청에 안 실린다)
 *   · createThread 가 connectionId 를 **본문 최상위**로 받는다 → 아래에서 옮겨 준다
 *
 * 인증: 백엔드가 QBase JSESSIONID 쿠키로 호출자를 확정한다(스탠드얼론은 단일 사용자).
 * 패키지가 모든 요청에 credentials:'include' 를 붙인다.
 */
import i18n from '../i18n';
import { withGuardrail } from '../utils/guardrailGate';

/*
 * **AI 제외 빌드에서는 공통 패키지를 번들에 넣지 않는다**(옵션 2, config/features.js). 조건이 빌드 때
 * 상수("true" === "true")가 되므로 웹팩이 아래 require 를 따라가지 않는다 — 그래서 그 빌드는 qbase 저장소
 * 없이도 된다. **조건을 함수나 변수로 감싸지 말 것** — 웹팩이 상수로 못 알아보고 패키지를 다시 싣는다.
 */
const shared = process.env.REACT_APP_NO_ASSISTANT === 'true' ? null : require('@qbase/assistant-ui');

/** AI 제외 빌드의 클라이언트 — 부르면 분명한 사유로 실패한다(화면이 감춰서 불리지 않는 것이 정상이다). */
function disabledClient() {
  const off = () => Promise.reject(Object.assign(new Error(i18n.t('common:errors.AI_NOT_INCLUDED')), { errorType: 'AI_NOT_INCLUDED' }));
  const names = ['runTurn', 'getThread', 'createThread', 'listThreads', 'deleteThread', 'answerQuestion', 'abortTurn',
    'getAccount', 'startLogin', 'submitLoginCode', 'cancelLogin', 'logout', 'putCredential', 'deleteCredential'];
  return Object.fromEntries(names.map((n) => [n, off]));
}

const BASE_URL = process.env.REACT_APP_API_BASE_URL || '/sql-developer';

const base = shared ? shared.createAssistantClient({
  baseUrl: BASE_URL,
  basePath: '/assistant',
  // tenantId 없음 — 자체 백엔드에는 테넌트 개념이 없다
  formatHttpError: (status) => i18n.t('common:qb.requestFailed', { status }),
}) : disabledClient();

/** 이 앱의 클라이언트. 훅에 넘길 때도 이것을 쓴다. */
/**
 * SSE 이벤트 탭.
 *
 * 공통 훅(@qbase/assistant-ui)은 자기가 아는 이벤트만 처리하고 나머지는 버린다.
 * LiveSQL 에만 있는 case_draft(사례 초안) · warning(임시 인덱스 잔존)을 받으려면
 * 훅보다 먼저 볼 수 있어야 한다. 패키지는 다른 저장소라 고치지 않고 여기서 감싼다.
 */
const eventTaps = new Set();

/** @returns {() => void} 해제 함수 */
export function onAssistantEvent(fn) {
  eventTaps.add(fn);
  return () => eventTaps.delete(fn);
}

/** 튜닝 사례 원본 등록 (명세 1). TEXT·이미지·PDF·PPTX. */
export async function uploadTuningSources(files) {
  const form = new FormData();
  for (const f of files) form.append('files', f, f.name);
  const res = await fetch(`${BASE_URL}/assistant/tuning-cases/sources`, {
    method: 'POST', body: form, credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error((body && body.error) || i18n.t('common:qb.requestFailed', { status: res.status }));
  }
  return res.json();
}

/**
 * 사례의 그림 올리기(원문의 설명용 이미지) — 대기 상태로 들어가고, 사례를 저장할 때 `figures` 로 붙는다.
 * { figures: [{ id, filename, bytes } | { filename, error }] }
 */
export async function uploadCaseFigures(files) {
  const form = new FormData();
  for (const f of files) form.append('files', f, f.name || 'figure.png');
  const res = await fetch(`${BASE_URL}/assistant/tuning-cases/figures`, { method: 'POST', body: form, credentials: 'include' });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error((body && body.error) || i18n.t('common:qb.requestFailed', { status: res.status }));
  }
  return res.json();
}

/** 사례 목록·검색. q 가 있으면 FTS 검색, 없으면 최신순 목록. */
/** 쿼리 작성에 쓰는 메타 정보원의 목록·가용성 (패널 상단 리본). */
export async function getMetaSources({ connectionId, dataModelId, stdNm } = {}) {
  const p = new URLSearchParams();
  if (connectionId) p.set('connectionId', connectionId);
  if (dataModelId) p.set('dataModelId', dataModelId);
  if (stdNm) p.set('stdNm', stdNm);
  return jsonGet(`/assistant/meta-sources${p.toString() ? `?${p}` : ''}`);
}

export async function listTuningCases({ q, visibility, status, includeDrafts, limit, topics, folder, usage } = {}) {
  const p = new URLSearchParams();
  // 용도(ref·edu·challenge) — 정하지 않은 사례는 서버가 내용에서 정한 값으로 거른다
  if (usage) p.set('usage', usage);
  // 폴더(하위 포함) — 주인 것만 걸린다
  if (folder) p.set('folder', folder);
  if (topics && topics.length) p.set('topics', topics.join(','));
  if (q) p.set('q', q);
  if (visibility) p.set('visibility', visibility);
  if (status) p.set('status', status);
  if (includeDrafts) p.set('includeDrafts', 'true');
  if (limit) p.set('limit', String(limit));
  return jsonGet(`/assistant/tuning-cases${p.toString() ? `?${p}` : ''}`);
}

// ── 사례 폴더(2026-10-11) — 주인만 보는 정리 경로. 이름에 고객사가 들어갈 수 있어 남·AI 에게는 나가지 않는다
export async function getCaseFolders() { return jsonGet('/assistant/tuning-cases/folders'); }
export async function createCaseFolder(path) { return jsonSend('POST', '/assistant/tuning-cases/folders', { path }); }
export async function renameCaseFolder(from, to) { return jsonSend('PATCH', '/assistant/tuning-cases/folders', { from, to }); }
export async function deleteCaseFolder(path) { return jsonSend('DELETE', `/assistant/tuning-cases/folders?path=${encodeURIComponent(path)}`); }

export async function getTuningCase(id) {
  const r = await jsonGet(`/assistant/tuning-cases/${encodeURIComponent(id)}`);
  return r.case;
}

/** 사례의 지난 판(2026-10-24) — 주인만. 목록은 본문 없이, 하나는 사례 모양({ ...case, version }) */
export async function listCaseVersions(id) {
  return jsonGet(`/assistant/tuning-cases/${encodeURIComponent(id)}/versions`);
}
export async function getCaseVersion(id, versionId) {
  const r = await jsonGet(`/assistant/tuning-cases/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}`);
  return r.case;
}

export async function deleteTuningCase(id) {
  const res = await fetch(`${BASE_URL}/assistant/tuning-cases/${encodeURIComponent(id)}`, {
    method: 'DELETE', credentials: 'include',
  });
  if (!res.ok) throw new Error(i18n.t('common:qb.requestFailed', { status: res.status }));
  return res.json();
}

/** GET + 오류 문구 통일. 사례 라우트는 공통 패키지가 모르는 자체 API 라 여기서 부른다. */
async function jsonGet(path) { return withGuardrail(() => jsonGetRaw(path)); }
async function jsonGetRaw(path) {
  const res = await fetch(`${BASE_URL}${path}`, { credentials: 'include' });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const err = new Error((body && body.error) || i18n.t('common:qb.requestFailed', { status: res.status }));
    // jsonSend 와 같은 모양 — 상태·본문이 없으면 formatApiError 가 서버의 사유(만료 등)를 「서버에 연결할 수 없다」 로 바꾼다
    // (신고 2026-10-06: 원본 패널이 만료된 수집분을 열 때 그렇게 보였다)
    err.body = body; err.errorType = body && body.errorType; err.status = res.status;
    throw err;
  }
  return res.json();
}

/** 본문 있는 요청 + 오류 문구 통일. */
async function jsonSend(method, path, body, opts) { return withGuardrail(() => jsonSendRaw(method, path, body, opts)); }
async function jsonSendRaw(method, path, body, opts = {}) {
  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'include',
    // 중지 — 요청을 끊으면 서버가 'close' 를 보고 모델 호출도 끊는다(사례 이름 바꾸기 매핑 제안)
    signal: opts.signal,
  });
  const parsed = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error((parsed && parsed.error) || i18n.t('common:qb.requestFailed', { status: res.status }));
    // 문구만 남기면 **본문에 실린 근거를 버리게 된다.** 사례 저장 거부(REDACTION_FAILED)는
    // 어떤 식별자가 남았는지를 leaked 로 알려 주는데, 그게 없으면 사용자가 고칠 수가 없다.
    err.body = parsed;
    err.errorType = parsed && parsed.errorType;
    err.status = res.status;
    throw err;
  }
  return parsed;
}

// ── 내 API 키 (LLM 경로 2) ──────────────────────────────────────────────
// 키는 **서버로만** 간다. localStorage 에 두지 않는다 — XSS 로 읽히고 사용자별로 나뉘지도 않는다.

/** 고를 수 있는 경로. **백엔드 authSource.SOURCES 와 같아야 한다** — 어긋나면 화면에는 뜨는데 서버가 400 을 준다. */
export const AUTH_SOURCES = ['login', 'token', 'apiKey'];

/**
 * 고를 수 있는 모델 — 제공자에게 직접 물은 목록(백엔드 harness/models.js). 화면에 모델 이름을 적어 두지 않는다.
 * 못 받으면 `models: []` 와 `reason` 이 온다(오류가 아니다). 모델을 부르지 않으므로 가드레일 관문을 지나지 않는다.
 */
export const getModels = (provider = 'claude', refresh = false) => jsonGetRaw(`/assistant/models?provider=${encodeURIComponent(provider)}${refresh ? '&refresh=1' : ''}`);

export const saveApiKey = (apiKey, provider = 'claude') => jsonSend('PUT', '/assistant/api-key', { provider, apiKey });
export const deleteApiKey = (provider = 'claude') => jsonSend('DELETE', `/assistant/api-key?provider=${encodeURIComponent(provider)}`);
/** 쓸 경로를 고정한다. 'auto' 면 고정을 푼다. */
export const setAuthSource = (source, provider = 'claude') => jsonSend('PUT', '/assistant/account/source', { provider, source });

// ── 실행계획 옆의 부가 정보 (기능 14) ──────────────────────────────────
// 증상 추출과 사례 경계는 **서버가** 한다 — 에이전트와 같은 함수·같은 경계를 봐야 한다.

/** 실행계획 텍스트 → 증상 + 비슷한 사례. */
export const getPlanInsights = (planText, limit) => jsonSend('POST', '/assistant/plan-insights', { planText, limit });

/** 이 SQL 이 운영의 어느 sql_id 인가. 확정 못 하면 후보만 온다 — 자동으로 붙이지 않는다. */
export const resolveFingerprint = ({ connectionId, sqlText, sqlId, own }) =>
  jsonSend('POST', '/monitoring/sql-perf/fingerprint', { connectionId, sqlText, sqlId, own });

/** 실행 이력 — ASH → AWR → (선택) 트레이스. */
export const getSqlHistory = ({ connectionId, sqlId, minutes, days, includeTrace }) => {
  const p = new URLSearchParams({ connectionId, sqlId });
  if (minutes) p.set('minutes', String(minutes));
  if (days) p.set('days', String(days));
  if (includeTrace) p.set('includeTrace', 'true');
  return jsonGet(`/monitoring/sql-perf/history?${p}`);
};

// ── 사례 등록 (화면이 절차를 쥔다) ──────────────────────────────────────
// 등록 흐름은 정해져 있으므로 대화가 아니라 화면이 단계를 진행한다.
// LLM 은 「첨부에서 양식을 채운다」 한 가지만 한다.

/** 첨부(와 지금 양식)로 초안을 만든다. current+instruction 을 주면 재작성이다. */
export const buildCaseDraft = ({ sourceIds, visibility, redact, current, instruction, provider, caseIndex }) =>
  jsonSend('POST', '/assistant/tuning-cases/draft', {
    sourceIds, visibility, redact, current, instruction, provider,
    // 자료에 사례가 여럿이면 그중 몇 번째를 채울지. 응답의 caseList 가 전부를 알려 준다.
    caseIndex,
  });

/**
 * 사례 이름 바꾸기(변조) 매핑 제안 — AI 는 {from,to} 목록만 낸다. 바꾸기는 화면(utils/caseRedact)이 기계적으로 한다.
 * current 를 주면 「다시 제안」(지금 매핑 + 지시). signal 로 중지한다.
 */
export const suggestCaseRedaction = ({ fields, instruction, current, provider }, signal) =>
  jsonSend('POST', '/assistant/tuning-cases/redact-suggest', { fields, instruction, current, provider }, { signal });

/**
 * 이름 바꾸기 매핑 규칙 — 원본 폴더 단위로 누적(본인만). folder 를 주면 그 폴더의 기본 규칙을 만들어 둔 목록이 온다(본문 없이 건수만).
 * 저장 때 누적은 사례 저장(PUT /tuning-cases 의 renameRuleId·renameMapping)이 한다.
 */
export const getRenameRules = (folder) => jsonGet(`/assistant/tuning-cases/rename-rules${folder === undefined ? '' : `?folder=${encodeURIComponent(folder || '')}`}`);
export const getRenameRule = (id) => jsonGet(`/assistant/tuning-cases/rename-rules/${encodeURIComponent(id)}`);
export const createRenameRule = ({ folder, name }) => jsonSend('POST', '/assistant/tuning-cases/rename-rules', { folder, name });

/**
 * **답을 가린 채 다시 풀어 본다.** 개선 전 쿼리·실행계획만 새 호출에 주고 증상·원인·처방을
 * 다시 뽑게 한 뒤 지금 분류와 대조한다. 양식을 통째로 보내도 **서버의 `holdout` 이 나가는 칸을
 * 정한다** — 개선 후 내용은 프롬프트에 실리지 않는다.
 *
 * 돌아오는 것은 판정이 아니라 **차이**다. 점수도 없고 저장을 막지도 않는다.
 */
export const validateCaseDraft = ({ fields, topics, provider }) =>
  jsonSend('POST', '/assistant/tuning-cases/validate', { fields, topics, provider });

/**
 * 튜닝 가이드 — **매 턴 프롬프트에 실리는 규칙**(사례는 찾아서 대조, 가이드는 처음부터 제약).
 *
 * 목록과 함께 **지금 실리는 블록 그대로**(preview)와 길이를 준다. 무엇이 실리는지 모르면
 * 답이 왜 이렇게 나왔는지도, 규칙을 어떻게 고쳐야 할지도 알 수 없다.
 */
export const getTuningGuides = () => jsonGet('/assistant/tuning-guides');
export const saveTuningGuide = (payload) => jsonSend('PUT', '/assistant/tuning-guides', payload);
export const deleteTuningGuide = (id) => jsonSend('DELETE', `/assistant/tuning-guides/${encodeURIComponent(id)}`);

/**
 * 테이블 분석 — **SQL 한 건이 아니라 테이블이 단위**다.
 *
 * 수집(analyze)과 리포트(report)가 나뉜 이유: 수집은 DB 를 읽는 일이고 리포트는 LLM 을 부르는
 * 일이라 비용도 실패 방식도 다르다. 한 번 수집해 두고 절을 바꿔 가며 여러 번 리포트한다.
 */
/**
 * 대상 고르기 — 스키마를 안 주면 스키마 목록, 주면 그 안의 테이블 목록.
 * **손으로 적게 하지 않는다** — 오타 하나에 "테이블을 찾지 못했습니다" 가 나오면
 * 사용자는 이름이 틀린 건지 권한이 없는 건지 알 수 없다.
 */
export const listAnalysisObjects = ({ connectionId, schema, q }) => {
  const sp = new URLSearchParams({ connectionId });
  if (schema) sp.set('schema', schema);
  if (q) sp.set('q', q);
  return jsonGet(`/assistant/table-analysis/objects?${sp.toString()}`);
};

/**
 * 저장된 관계 후보 — **분석을 다시 돌리지 않고** 읽는다.
 *
 * ERD 가 이것을 점선으로 함께 그린다. FK 가 하나도 선언돼 있지 않은 스키마(교육DB 가
 * 그렇다 — 실측 0건)에서는 이것이 없으면 상자만 뜨고 선이 하나도 없다.
 */
export const fetchRelationships = ({ connectionId, tables, limit }) => {
  const sp = new URLSearchParams({ connectionId });
  if (tables && tables.length) sp.set('tables', tables.join(','));
  if (limit) sp.set('limit', String(limit));
  return jsonGet(`/assistant/relationships?${sp.toString()}`);
};

/** 관계 추정을 **지금 돌린다.** DB 를 읽으므로 시간이 걸린다 — 화면이 진행을 보여줘야 한다. */
export const analyzeRelationships = ({ connectionId, schema, verify }) =>
  jsonSend('POST', '/assistant/relationships/analyze', { connectionId, schema, verify: verify || 'none' });

/**
 * 테이블 조사 원본 — 사용자별 저장(2026-10-06). 수집은 저장하지 않고 collectId 를 준다 → 「저장」 때 테이블별 교체/병합.
 * choices: { 'OWNER.TABLE': 'replace' | 'merge' | 'skip' } — 이미 저장된 테이블은 반드시 고른다(안 고르면 409 NEED_CHOICE)
 */
export const saveTableAnalysis = ({ connectionId, collectId, choices }) =>
  jsonSend('POST', '/assistant/table-analysis/save', { connectionId, collectId, choices });
/** 저장된 분석(+진단만 있는 테이블). q — 테이블 이름·SQL 전문/조건 컬럼·진단 본문 검색(2026-10-07). 항목마다 판별 최신 진단 머리글(diag) */
export const listSavedTables = (connectionId, q) => {
  const sp = new URLSearchParams({ connectionId });
  if (q) sp.set('q', q);
  return jsonGet(`/assistant/table-analysis/saved?${sp.toString()}`);
};
/** 진단 이력(2026-10-07) — 테이블 하나의 머리글 목록 · 한 건 본문 · 테이블마다 판별 최신(본문 포함) */
export const listTableDiagnoses = ({ connectionId, owner, table }) =>
  jsonGet(`/assistant/table-analysis/diagnoses?${new URLSearchParams({ connectionId, owner, table }).toString()}`);
export const getTableDiagnosis = (id) => jsonGet(`/assistant/table-analysis/diagnoses/${encodeURIComponent(id)}`);
export const latestTableDiagnoses = ({ connectionId, tables }) =>
  jsonSend('POST', '/assistant/table-analysis/diagnoses/latest', { connectionId, tables });
export const loadSavedTables = ({ connectionId, tables }) =>
  jsonSend('POST', '/assistant/table-analysis/saved/load', { connectionId, tables });
export const deleteSavedTable = ({ connectionId, owner, table }) => jsonSend('DELETE',
  `/assistant/table-analysis/saved?${new URLSearchParams({ connectionId, owner, table }).toString()}`);
/** 원본 보기(주인만) — collectId 가 있으면 저장 전 수집분 */
export const getTableRaw = ({ connectionId, owner, table, collectId }) => {
  const sp = new URLSearchParams({ connectionId, owner, table });
  if (collectId) sp.set('collectId', collectId);
  return jsonGet(`/assistant/table-analysis/raw?${sp.toString()}`);
};

/** dist — 설계 모드만. 날짜 컬럼의 기간별 건수를 센다(테이블 본체를 읽는다 — 큰 테이블은 표본) */
export const analyzeTables = ({ connectionId, schema, tables, dist }) =>
  jsonSend('POST', '/assistant/table-analysis/analyze', { connectionId, schema, tables, dist: !!dist });

/** 규칙 진단(AI 없음) — 수집한 요약에 정해진 규칙을 적용한다. AI 진단과 같은 모양을 돌려준다. connectionId 를 주면 진단 이력에 남는다 */
export const ruleDiagnose = ({ tables, sections, connectionId }) =>
  jsonSend('POST', '/assistant/table-analysis/diagnose', { tables, sections, connectionId });

/**
 * design('index'|'partition') 이면 설계서 초안을 만들고 설계 이력에 남긴다(테이블 하나·연결 필요). 진단이면 진단 이력에 남는다.
 * priorDiagId — 설계가 근거로 쓸 진단(진단 이력의 id). 없거나 맞지 않으면 서버가 규칙 진단을 만들어 쓴다
 */
export const buildTableReport = ({ tables, sections, provider, note, design, connectionId, guideIds, priorDiagId }) =>
  jsonSend('POST', '/assistant/table-analysis/report', { tables, sections, provider, note, design, connectionId, guideIds, priorDiagId });

/** 설계 이력 — 자기 것만. 목록은 연결 단위(테이블을 주면 그 테이블만) */
export const listTableDesigns = ({ connectionId, owner, table }) => {
  const sp = new URLSearchParams({ connectionId });
  if (owner && table) { sp.set('owner', owner); sp.set('table', table); }
  return jsonGet(`/assistant/table-analysis/designs?${sp.toString()}`);
};
export const getTableDesign = (id) => jsonGet(`/assistant/table-analysis/designs/${encodeURIComponent(id)}`);

/**
 * 설계 가이드(2026-10-06) — 인덱스·파티션 설계에 실리는 규칙 묶음. 자기 것만. 매핑(DB·스키마)은 설계 화면의 기본 선택.
 * 파일 변환은 **초안만** 돌려준다 — 저장은 사람이 고친 뒤 saveDesignGuide 로.
 */
export const listDesignGuides = (kind) => jsonGet(`/assistant/design-guides${kind ? `?kind=${encodeURIComponent(kind)}` : ''}`);
export const saveDesignGuide = (payload) => jsonSend('PUT', '/assistant/design-guides', payload);
/** 사본 보내기 — 받는 사람(usrid)마다 작성중 사본. { sent: [{to,id}], skipped: [{to,reason}] } */
export const copyDesignGuide = (id, to) => jsonSend('POST', `/assistant/design-guides/${encodeURIComponent(id)}/copy`, { to });
/** 작성중 → 배포(되돌리지 않는다). 배포된 가이드만 설계 창에 뜬다 */
export const publishDesignGuide = (id) => jsonSend('POST', `/assistant/design-guides/${encodeURIComponent(id)}/publish`, {});
/** 범용 튜닝 가이드(읽기 전용) → 내 가이드(작성중 사본) */
export const copyBuiltinGuide = (id, title) => jsonSend('POST', '/assistant/design-guides/builtin/copy', { id, title });
// 공용 가이드(2026-10-15) — 관리자가 내놓은 가이드를 내 가이드로 복사 · 관리자가 내놓기/거두기
export const copySharedGuide = (id, title) => jsonSend('POST', '/assistant/design-guides/shared/copy', { id, title });
export const setGuideShared = (id, on) => jsonSend('PUT', `/assistant/design-guides/${encodeURIComponent(id)}/shared`, { on: !!on });
/** 이전 튜닝 규칙(짧은 규칙 목록) → 튜닝 가이드 하나(작성중). 옮긴 규칙은 꺼진다 */
export const migrateLegacyGuides = () => jsonSend('POST', '/assistant/design-guides/migrate-legacy', {});
export const deleteDesignGuide = (id) => jsonSend('DELETE', `/assistant/design-guides/${encodeURIComponent(id)}`);
/** 규칙 작성 가이드 — 사용자별이 아닌 공용 문서. { guide: { body, isDefault, updatedBy, updatedAt }, canEdit }. 고치기는 관리자만 */
export const getWritingGuide = () => jsonGet('/assistant/design-guides/writing-guide');
export const saveWritingGuide = (body) => jsonSend('PUT', '/assistant/design-guides/writing-guide', { body });
/** 텍스트 문서(마크다운) — { text, title, kind }. 근거·원천·변경이력까지 */
export const exportDesignGuide = (id) => jsonGet(`/assistant/design-guides/${encodeURIComponent(id)}/export`);
/**
 * mode — 'rebuild'(완전 재구성) | 'augment'(추가/보완: current 규칙에 보탠다). 배포된 가이드(guideId)의 완전 재구성은 서버가 409 GUIDE_PUBLISHED
 */
export async function convertDesignGuide({ files, kind, provider, lang, mode, guideId, current }) {
  const form = new FormData();
  for (const f of files) form.append('files', f, f.name);
  form.append('kind', kind);
  form.append('mode', mode === 'augment' ? 'augment' : 'rebuild');
  if (guideId) form.append('guideId', guideId);
  if (mode === 'augment' && current) form.append('current', JSON.stringify(current));
  if (provider) form.append('provider', provider);
  if (lang) form.append('lang', lang);
  // FormData 는 다시 보낼 수 있다(같은 File 객체) — 가드레일로 멈추면 확인 뒤 한 번 더
  return withGuardrail(() => postConvert(form));
}
async function postConvert(form) {
  const res = await fetch(`${BASE_URL}/assistant/design-guides/convert`, { method: 'POST', body: form, credentials: 'include' });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error((body && body.error) || i18n.t('common:qb.requestFailed', { status: res.status }));
    err.body = body; err.errorType = body && body.errorType; err.status = res.status;
    throw err;
  }
  return body;
}
export const saveTableDesign = (id, { title, doc }) =>
  jsonSend('PUT', `/assistant/table-analysis/designs/${encodeURIComponent(id)}`, { title, doc });
export const deleteTableDesign = (id) => jsonSend('DELETE', `/assistant/table-analysis/designs/${encodeURIComponent(id)}`);

/**
 * AWR 리포트 분석의 AI 요약 — 사실은 /monitoring/awr/facts 가 준 것을 그대로 넘긴다.
 * SQL 원문은 includeSqlText 를 켰을 때만 서버가 싣는다(backend assistant/awr/summary.js 의 화이트리스트).
 */
export const summarizeAwr = ({ facts, sections, includeSqlText, provider, lang }) =>
  jsonSend('POST', '/assistant/awr-summary', { facts, sections, includeSqlText: !!includeSqlText, provider, lang });

/** 수집 마법사 AI 보조 — 칸을 채우기만 한다. 나온 SQL 은 서버가 읽기 전용인지 거른다(rejected) */
export const monitoringWizardAssist = ({ kind, input, provider, lang }) =>
  jsonSend('POST', '/assistant/monitoring-wizard', { kind, input, provider, lang });

/** 챌린지 「AI 검토」 — 본인 계정으로 고칠 점을 받는다. 반영은 편집기에서 사용자가 고른다(utils/challengeEdit.applyReviewFixes) */
export const reviewChallenge = ({ challenge, criteria, provider, lang }) =>
  jsonSend('POST', '/assistant/challenge-review', { challenge, criteria, provider, lang });

/** 분석 결과 → 사례 초안. **LLM 을 부르지 않는다** — 이미 구조화된 것을 양식에 옮길 뿐이다. */
export const tableCaseDraft = ({ table, report, section }) =>
  jsonSend('POST', '/assistant/table-analysis/case-draft', { table, report, section });

/** 보관한 실행계획 → 사례 초안. 이것도 **LLM 을 부르지 않는다**(양식에 옮겨 담기만). */
export const planArchiveCaseDraft = (id) =>
  jsonSend('POST', '/assistant/plan-archive/case-draft', { id });

/** 사례 저장. generic 은 서버가 변조 매핑을 기계로 검증한다 — 통과 못 하면 422 다. */
export const saveTuningCase = (payload) => jsonSend('PUT', '/assistant/tuning-cases', payload);

/**
 * 분류 유형 목록(증상·원인·처방)과 코드별 사례 수.
 * **목록도 라벨도 서버가 준다** — 사용자가 유형을 늘리므로 화면에 적어 둘 수가 없다.
 * @param all 비활성까지 (유형 관리 화면)
 */
/** 분류 목록 + 코드별 사례 수. filters(folder·visibility·status·usage)를 주면 목록과 같은 조건으로 센다 */
export const getCaseTopics = (all, filters = {}) => {
  const qs = new URLSearchParams();
  if (all) qs.set('all', 'true');
  ['folder', 'visibility', 'status', 'usage'].forEach((k) => { if (filters[k]) qs.set(k, filters[k]); });
  const s = qs.toString();
  return jsonGet(`/assistant/tuning-cases/topics${s ? `?${s}` : ''}`);
};

/** 유형 추가·수정. **지우는 길은 없다** — active:false 로 내린다. */
export const saveCaseTopic = (code, body) =>
  jsonSend('PUT', `/assistant/tuning-cases/topics/${encodeURIComponent(code)}`, body);

/**
 * URL 을 원본으로 등록한다. 본문 텍스트와 **본문 이미지까지** 가져온다 —
 * 튜닝 블로그는 SQL·실행계획을 이미지로 올리는 경우가 많다.
 */
export const addTuningSourceUrl = ({ url, withImages = true }) =>
  jsonSend('POST', '/assistant/tuning-cases/sources/url', { url, withImages });

// ── 구독 티어 ────────────────────────────────────────────────────────────
// 부여 표는 **설정**이다(결정 ⑦). 관리 화면이 이 라우트를 쓴다 — 관리자가 아니면 403 이
// 오고, 화면은 그것으로 메뉴를 감춘다.

/** 내 티어와 부여받은 기능. 누구나 볼 수 있다. */
export const getMyTier = () => jsonGet('/assistant/tier/me');
/** 부여 표 전체 — 관리자만. 403 이면 관리자가 아니다. */
export const getTierAdmin = () => jsonGet('/assistant/tier');
export const setTierFeatures = (tierId, features) =>
  jsonSend('PUT', `/assistant/tier/${encodeURIComponent(tierId)}/features`, { features });
export const setTierLimits = (tierId, limits) =>
  jsonSend('PUT', `/assistant/tier/${encodeURIComponent(tierId)}/limits`, limits);
export const setUserTier = (usrid, tierId) =>
  jsonSend('PUT', `/assistant/tier/user/${encodeURIComponent(usrid)}`, { tierId });

/** 사용 안내 — 도움말 창과 도움말 상태기계가 같은 것을 본다. 로그인 없이 읽는다. */
export async function getUsageGuide(dbType) {
  const q = dbType ? `?dbType=${encodeURIComponent(dbType)}` : '';
  const res = await fetch(`${BASE_URL}/assistant/usage-guide${q}`, { credentials: 'include' });
  if (!res.ok) throw new Error(i18n.t('common:qb.requestFailed', { status: res.status }));
  return res.json();
}

/**
 * 원본 PPTX 의 슬라이드(2026-10-13) — 원문이 도형으로 그려 설명하던 도식을 사례의 그림으로 옮긴다. 본인 첨부만. AI 와 무관한 길이다.
 *   getSourceSlides(id)      목록 [{ no, fileNo, title, isDiagram, … }]
 *   getSourceSlide(id, no)   그 슬라이드의 SVG { svg, width, height, title, skipped } — 화면이 PNG 로 바꿔 그림으로 올린다
 *   slideThumbUrl(id, no)    미리보기 <img> 주소
 */
export async function getSourceSlides(id) {
  const res = await fetch(`${BASE_URL}/assistant/tuning-cases/sources/${encodeURIComponent(id)}/slides`, { credentials: 'include' });
  if (!res.ok) throw new Error(i18n.t('common:qb.requestFailed', { status: res.status }));
  return res.json();
}
export async function getSourceSlide(id, no) {
  const res = await fetch(`${BASE_URL}/assistant/tuning-cases/sources/${encodeURIComponent(id)}/slides/${encodeURIComponent(no)}`, { credentials: 'include' });
  if (!res.ok) throw new Error(i18n.t('common:qb.requestFailed', { status: res.status }));
  return res.json();
}
export const slideThumbUrl = (id, no) => `${BASE_URL}/assistant/tuning-cases/sources/${encodeURIComponent(id)}/slides/${encodeURIComponent(no)}?format=svg`;

export async function deleteTuningSource(id) {
  const res = await fetch(`${BASE_URL}/assistant/tuning-cases/sources/${encodeURIComponent(id)}`, {
    method: 'DELETE', credentials: 'include',
  });
  if (!res.ok) throw new Error(i18n.t('common:qb.requestFailed', { status: res.status }));
  return res.json();
}

/**
 * 메시지 id → 첨부 목록. 공통 훅은 저장 메시지의 payload 를 버리므로(fromStored) 첨부가
 * 화면까지 못 온다. 그래서 LiveSQL 쪽에서 따로 색인한다 — 두 경로로 채워진다:
 *   · 스레드를 열 때: 서버가 준 payload.attachments (getThread 래퍼)
 *   · 보낼 때: turn_start 가 준 userMessageId 에 방금 보낸 첨부를 건다 (패널)
 */
const attachmentIndex = new Map();

export function rememberAttachments(messageId, list) {
  if (messageId && Array.isArray(list) && list.length) attachmentIndex.set(String(messageId), list);
}

export function getAttachmentsFor(messageId) {
  return attachmentIndex.get(String(messageId)) || null;
}

function indexStoredAttachments(thread) {
  for (const m of (thread && thread.messages) || []) {
    if (m.role === 'user' && m.payload && Array.isArray(m.payload.attachments)) rememberAttachments(m.id, m.payload.attachments);
  }
  return thread;
}

// 덮어쓰기는 **base 다음에** 와야 한다. 전에는 runTurn 을 먼저 쓰고 ...base 를 뒤에 펼쳐서
// base.runTurn 이 탭을 도로 덮었다 — case_draft·warning 탭이 조용히 죽어 있었다.
export const client = {
  ...base,
  /** 모든 턴 이벤트를 탭에 먼저 흘리고 원래 핸들러로 넘긴다. */
  // AI 가드레일로 멈추면(확인 전·결정 필요) 창을 띄우고 같은 턴을 다시 보낸다 — 서버는 그 경우 메시지를 저장하기 전에 멈춘다
  runTurn: (threadId, text, opts = {}) => withGuardrail(() => base.runTurn(threadId, text, {
    ...opts,
    onEvent: (ev) => {
      eventTaps.forEach((fn) => { try { fn(ev); } catch (_e) { /* 탭 하나가 죽어도 스트림은 계속 */ } });
      if (opts.onEvent) opts.onEvent(ev);
    },
  })),
  getThread: (id) => base.getThread(id).then(indexStoredAttachments),
  /**
   * 자체 백엔드는 `connectionId` 를 본문 최상위로 받는다(공통 서비스는 context 안).
   * 훅은 공통 규약대로 context 에 담아 보내므로 여기서 꺼내 올린다.
   */
  createThread: (body = {}) => {
    const { context, ...rest } = body;
    return base.createThread({
      ...rest,
      ...(context && context.connectionId ? { connectionId: context.connectionId } : {}),
    });
  },
};

// ---- 옛 이름 유지 — AssistantSettingsSection 은 한 줄도 바뀌지 않는다 ----
export const getAccount = client.getAccount;
export const startLogin = client.startLogin;
export const submitLoginCode = client.submitLoginCode;
export const cancelLogin = client.cancelLogin;
export const logout = client.logout;
export const putCredential = client.saveToken;      // 공통 패키지에서는 saveToken
export const deleteCredential = client.removeToken; // 공통 패키지에서는 removeToken
export const getThread = client.getThread;
export const deleteThread = client.deleteThread;
export const answerQuestion = client.answerQuestion;
export const abortTurn = client.abortTurn;

/** 옛 이름: sendMessage(threadId, payload, {signal, onEvent}) */
export const sendMessage = (threadId, payload, { signal, onEvent }) =>
  client.runTurn(threadId, payload.text, {
    context: payload.context,
    signal,
    onEvent: (ev) => onEvent(ev.type, ev.data),
  });

/** 옛 반환 모양({threads})을 유지한다 — 패키지는 배열을 그대로 준다 */
export const listThreads = () => client.listThreads().then((threads) => ({ threads }));

export const AssistantError = shared ? shared.AssistantError : class AssistantError extends Error {};
